import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { useSocialPaths } from "@/lib/socialPaths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft } from "lucide-react";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";

export default function OrgSocialMessagesPage() {
  const { conversationId: conversationIdParam } = useParams();
  const conversationId = conversationIdParam
    ? Number(conversationIdParam)
    : null;
  const { accessToken } = useAuth();
  const { orgId, canPublish, displayName } = useOrgSocialActor();
  const paths = useSocialPaths();
  const navigate = useNavigate();

  const [dms, setDms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dmPeerId, setDmPeerId] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const loadDms = useCallback(async () => {
    if (!accessToken || !orgId || !canPublish) {
      setDms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${orgId}/social/dm/conversations`,
        { headers }
      );
      setDms(res.data?.conversations || []);
    } catch (err) {
      console.error(err);
      setDms([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, canPublish, headers]);

  const loadMessages = useCallback(async () => {
    if (!accessToken || !orgId || !conversationId || !canPublish) {
      setMessages([]);
      return;
    }
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${orgId}/social/dm/conversations/${conversationId}/messages`,
        { headers }
      );
      setMessages(res.data?.messages || res.data || []);
    } catch (err) {
      console.error(err);
      setMessages([]);
    }
  }, [accessToken, orgId, conversationId, canPublish, headers]);

  useEffect(() => {
    loadDms();
  }, [loadDms]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const startDm = async () => {
    if (!dmPeerId.trim() || !canPublish) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/dm/conversations`,
        { peer_user_id: dmPeerId.trim() },
        { headers }
      );
      toastSuccess("Conversation started");
      setDmPeerId("");
      await loadDms();
      const id = res.data?.conversation_id;
      if (id != null) navigate(paths.messages(id));
    } catch (err) {
      toastError(err, "Could not start DM");
    }
  };

  const sendMessage = async () => {
    if (!draft.trim() || !conversationId || !canPublish || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/dm/conversations/${conversationId}/messages`,
        { content: draft.trim() },
        { headers }
      );
      setDraft("");
      await loadMessages();
    } catch (err) {
      toastError(err, "Could not send");
    } finally {
      setSending(false);
    }
  };

  const selected = dms.find(
    (c) => Number(c.conversation_id) === conversationId
  );

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const threadTitle =
    selected?.peer?.display_name || selected?.peer?.username || "Conversation";

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col md:mx-auto md:max-w-3xl md:flex-row md:px-4 md:py-4">
      <aside
        className={cn(
          "flex min-h-0 min-w-0 flex-col border-border md:w-72 md:shrink-0 md:border-r",
          conversationId ? "hidden md:flex" : "flex flex-1"
        )}
      >
        <div className="shrink-0 space-y-2 border-b px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">Messages</h2>
              <p className="mt-0.5 hidden break-words text-xs text-muted-foreground md:block">
                Only {displayName} can start conversations. People cannot
                cold-DM the organization.
              </p>
            </div>
            {canPublish ? (
              <Button
                type="button"
                size="sm"
                className="h-10 shrink-0 md:hidden"
                onClick={() => setNewOpen((v) => !v)}
              >
                New
              </Button>
            ) : null}
          </div>
          {canPublish ? (
            <div
              className={cn(
                "min-w-0 flex-wrap gap-2 sm:flex-nowrap",
                newOpen ? "flex" : "hidden md:flex"
              )}
            >
              <Input
                value={dmPeerId}
                onChange={(e) => setDmPeerId(e.target.value)}
                placeholder="Peer user id"
                className="min-w-0 flex-1 rounded-xl"
              />
              <Button type="button" className="h-10 shrink-0" onClick={startDm}>
                Message
              </Button>
            </div>
          ) : (
            <p className="hidden text-sm text-muted-foreground md:block">
              Messaging as the organization requires Social publish permission.
            </p>
          )}
        </div>
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-2">
          {(dms || []).map((c) => {
            const active = Number(c.conversation_id) === conversationId;
            const name =
              c.peer?.display_name || c.peer?.username || "Conversation";
            const handle = c.peer?.username;
            return (
              <li key={c.conversation_id} className="min-w-0">
                <Link
                  to={paths.messages(c.conversation_id)}
                  className={cn(
                    "flex min-w-0 items-baseline gap-1.5 rounded-lg border p-3 text-sm transition-colors",
                    active
                      ? "border-primary bg-primary/10"
                      : "hover:bg-muted/40"
                  )}
                >
                  <span className="min-w-0 truncate font-medium" title={name}>
                    {name}
                  </span>
                  {handle ? (
                    <span
                      className="min-w-0 truncate text-muted-foreground"
                      title={`@${handle}`}
                    >
                      @{handle}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </aside>

      <main
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          conversationId ? "flex" : "hidden md:flex"
        )}
      >
        {conversationId ? (
          <>
            <header className="flex min-w-0 shrink-0 items-center gap-2 border-b px-3 py-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-10 shrink-0 px-2 md:hidden"
                onClick={() => navigate(paths.messages())}
                aria-label="Back to conversations"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <p
                className="min-w-0 truncate text-sm font-medium"
                title={threadTitle}
              >
                {threadTitle}
              </p>
            </header>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-3 py-3">
              {(messages || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No messages yet.</p>
              ) : (
                (messages || []).map((m) => (
                  <div
                    key={m.message_id || m.id || `${m.created_at}-${m.content}`}
                    className="break-words rounded-lg bg-muted/40 px-3 py-2 text-sm"
                  >
                    {m.content}
                  </div>
                ))
              )}
            </div>
            {canPublish ? (
              <div className="flex min-w-0 shrink-0 flex-wrap gap-2 border-t px-3 pt-2 pb-[max(1rem,env(safe-area-inset-bottom,0px))] sm:flex-nowrap md:pb-3">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a message…"
                  className="min-w-0 flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <Button
                  type="button"
                  className="h-10 shrink-0"
                  onClick={sendMessage}
                  disabled={sending || !draft.trim()}
                >
                  Send
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="hidden flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground md:flex">
            Select a conversation
          </div>
        )}
      </main>
    </div>
  );
}
